import type { CompanySessionTokens, SessionTokens } from '@lp/contracts';
import { Eye, EyeOff } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { errorText, post } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useLocale, useT } from '@/lib/i18n';
import { Button, ErrorBox, Field, Input } from '@/components/ui';

export function LoginPage() {
  const t = useT();
  const { locale, set: setLocale } = useLocale();
  const signedIn = useAuth((s) => s.signedIn);
  const [company, setCompany] = useState(false);
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      signedIn(company ? await post<CompanySessionTokens>('/company/auth/login', { company: code, username, password }) : await post<SessionTokens>('/auth/login', { username, password }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-ink-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-600 text-sm font-bold text-white">LP</div>
          <div>
            <div className="text-lg font-semibold text-ink-900">{t('Lottery Platform')}</div>
            <div className="text-xs text-ink-500">{t('Command Center')}</div>
          </div>
        </div>
        <form onSubmit={(e) => void submit(e)} className="space-y-4 rounded-lg border border-ink-200 bg-white p-5">
          <div className="grid grid-cols-2 gap-1 rounded-md bg-ink-100 p-1 text-xs font-medium" role="tablist">
            <button type="button" role="tab" aria-selected={!company} onClick={() => setCompany(false)} className={!company ? 'rounded bg-white py-1.5 shadow-sm' : 'py-1.5 text-ink-500'}>
              {t('Platform')}
            </button>
            <button type="button" role="tab" aria-selected={company} onClick={() => setCompany(true)} className={company ? 'rounded bg-white py-1.5 shadow-sm' : 'py-1.5 text-ink-500'}>
              {t('Company')}
            </button>
          </div>
          {company && (
            <Field label={t('Company code')}>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={10} required />
            </Field>
          )}
          <Field label={t('Username')}>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoFocus required />
          </Field>
          <Field label={t('Password')}>
            <div className="relative">
              <Input type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required className="pr-9" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 grid w-9 place-items-center text-ink-400 hover:text-ink-700" aria-label={show ? t('Hide password') : t('Show password')}>
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </Field>
          {error && <ErrorBox text={error} />}
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? t('Signing in…') : t('Sign in')}
          </Button>
        </form>
        <div className="mt-3 text-center">
          <button onClick={() => setLocale(locale === 'km' ? 'en' : 'km')} className="text-xs text-brand-700 hover:underline">
            {locale === 'km' ? 'English' : 'ភាសាខ្មែរ'}
          </button>
        </div>
      </div>
    </div>
  );
}
